import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SvgIconComponent } from '../../../../shared/svg-icon/svg-icon.component';
import type { DocMatrixLink, DocMatrixNavigationTarget } from '../../../../models/api';

interface LinkGroup {
  sourcemodelId: number;
  name: string;
  links: DocMatrixLink[];
}

@Component({
  selector: 'app-file-links-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      header="Csatolt linkek"
      [visible]="visible"
      (visibleChange)="visibleChange.emit($event)"
      [modal]="true"
      [style]="{ width: '440px' }"
    >
      <div style="display: flex; flex-direction: column; gap: 1rem; padding: 0.5rem 0">
        @if (fileName) {
          <p style="margin: 0; color: var(--fm-text-muted)">
            <strong>{{ fileName }}</strong>
          </p>
        }
        @if (links.length > 0) {
          @if (activeLinks.length > 0) {
            <div class="file-links-dialog__group">
              @if (activeName) {
                <p class="file-links-dialog__group-title">{{ activeName }}</p>
              }
              <ul class="file-links-dialog__list">
                @for (link of activeLinks; track link.id) {
                  <li
                    class="file-links-dialog__item"
                    (click)="navigate(link)"
                    title="Megjelenítés a docmatrixban"
                  >
                    <app-svg-icon name="link" [size]="16" color="var(--fm-primary)" />
                    <span class="file-links-dialog__name">{{ link.displayed_name }}</span>
                    <app-svg-icon name="crosshair" [size]="16" class="file-links-dialog__navigate-icon" />
                  </li>
                }
              </ul>
            </div>
          }
          @for (group of otherGroups; track group.sourcemodelId) {
            <div class="file-links-dialog__group file-links-dialog__group--other">
              <p class="file-links-dialog__group-title">{{ group.name }}</p>
              <ul class="file-links-dialog__list">
                @for (link of group.links; track link.id) {
                  <li
                    class="file-links-dialog__item file-links-dialog__item--other"
                    (click)="navigate(link)"
                    title="Megjelenítés a docmatrixban (elszámolás váltással)"
                  >
                    <app-svg-icon name="link" [size]="16" color="var(--fm-text-muted)" />
                    <span class="file-links-dialog__name">{{ link.displayed_name }}</span>
                    <app-svg-icon name="crosshair" [size]="16" class="file-links-dialog__navigate-icon" />
                  </li>
                }
              </ul>
            </div>
          }
        } @else {
          <p style="margin: 0">Ehhez a fájlhoz nincs csatolt link.</p>
        }
      </div>
      <ng-template pTemplate="footer">
        <p-button label="BEZÁRÁS" severity="secondary" [text]="true" (onClick)="visibleChange.emit(false)" />
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .file-links-dialog__group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .file-links-dialog__group--other {
        border-top: 1px solid var(--fm-border, #e5e7eb);
        padding-top: 1rem;
      }
      .file-links-dialog__group-title {
        margin: 0;
        font-size: 0.85rem;
        font-weight: 700;
        color: var(--fm-text, #1f2937);
      }
      .file-links-dialog__list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .file-links-dialog__item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--fm-border, #e5e7eb);
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.15s;
      }
      .file-links-dialog__item:hover {
        background: var(--fm-hover, #f3f4f6);
      }
      .file-links-dialog__item:hover .file-links-dialog__navigate-icon {
        opacity: 1;
      }
      /* A más (jellemzően már lezárt) elszámoláshoz tartozó linkek halványabbak,
         de ugyanúgy kattinthatóak — az odaugrás elszámolás-váltással történik. */
      .file-links-dialog__item--other {
        opacity: 0.7;
      }
      .file-links-dialog__item--other:hover {
        opacity: 1;
      }
      .file-links-dialog__name {
        font-weight: 500;
        flex: 1;
      }
      .file-links-dialog__navigate-icon {
        opacity: 0;
        transition: opacity 0.15s;
        flex-shrink: 0;
      }
    `,
  ],
})
export class FileLinksDialog {
  @Input() visible = false;
  @Input() fileName = '';
  @Input() links: DocMatrixLink[] = [];
  /** A jelenleg kiválasztott elszámolás azonosítója — ennek linkjei kerülnek az első csoportba. */
  @Input() currentSourcemodelId: number | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() navigateToItem = new EventEmitter<DocMatrixNavigationTarget>();

  navigate(link: DocMatrixLink): void {
    this.navigateToItem.emit({ itemUuid: link.item_uuid, sourcemodelId: link.sourcemodel_id });
  }

  get activeLinks(): DocMatrixLink[] {
    return this.links.filter((link) => link.sourcemodel_id === this.currentSourcemodelId);
  }

  get activeName(): string {
    return this.activeLinks[0]?.sourcemodel_name ?? '';
  }

  get otherGroups(): LinkGroup[] {
    const groups = new Map<number, LinkGroup>();
    for (const link of this.links) {
      if (link.sourcemodel_id === this.currentSourcemodelId) continue;
      let group = groups.get(link.sourcemodel_id);
      if (!group) {
        group = { sourcemodelId: link.sourcemodel_id, name: link.sourcemodel_name, links: [] };
        groups.set(link.sourcemodel_id, group);
      }
      group.links.push(link);
    }
    return [...groups.values()];
  }
}
